import { useCallback, useMemo, useRef, useState } from 'react';
import { detectFileType } from '@orijinworks/frontend-commons';
import store, { showToastWithType } from '../../../../core/store';
import { findMappedWidgetById, mapWidget } from '../../utils/reentry-plan-detail.util';
import {
  commitWidgetAttachments,
  fetchAttachmentDownloadUrl,
  requestAttachmentPresignedUrls,
  unlinkWidgetAttachment,
  uploadFileToPresignedUrl,
} from './attachmentApi';
import {
  ATTACHMENT_ACTION_ERROR_MESSAGES,
  ATTACHMENT_ALLOWED_MIME_TYPES,
  ATTACHMENT_ITEM_STATUS,
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  ATTACHMENT_VALIDATION_MESSAGES,
} from './attachment.constants';
import {
  hydrateCommittedWidget,
  replaceAttachmentWidget,
  replaceAttachmentWidgetItems,
} from './attachments-widget.factory';

const TOAST_TYPE_ERROR = 'error';

const occupiesAttachmentSlot = (item) =>
  item?.status === ATTACHMENT_ITEM_STATUS.SUCCESS ||
  item?.status === ATTACHMENT_ITEM_STATUS.UPLOADING;

const createLocalAttachmentId = () => `local-${crypto.randomUUID()}`;

/**
 * Client checks that mirror the portal attachment defaults before any network call.
 * @param {File} file
 * @returns {string|null}
 */
export const validateAttachmentFile = (file) => {
  if (!file || typeof file.size !== 'number' || file.size < 1) {
    return ATTACHMENT_VALIDATION_MESSAGES.EMPTY;
  }
  if (file.size > ATTACHMENT_MAX_FILE_SIZE_BYTES) {
    return ATTACHMENT_VALIDATION_MESSAGES.SIZE;
  }
  if (!ATTACHMENT_ALLOWED_MIME_TYPES.includes(file.type)) {
    return ATTACHMENT_VALIDATION_MESSAGES.TYPE;
  }
  return null;
};

const toChip = (id, file, status, errorMessage) => ({
  id,
  fileName: file?.name ?? '',
  mimeType: file?.type ?? '',
  status,
  ...(errorMessage ? { errorMessage } : {}),
});

const toPresignFile = (file) => ({
  filename: file.name,
  fileSize: file.size,
  mimeType: file.type,
});

const readDownloadUrl = (response) => response?.downloadUrl ?? response?.data?.downloadUrl ?? '';

/**
 * Upload, remove, and open for one case-plan attachments widget.
 * Successful files are committed in one POST. The widget is replaced from that
 * response. Failed files stay as error chips until the user removes them.
 * Attachment ids are never sent on the widget config PATCH.
 *
 * @param {{
 *   casePlanId: string,
 *   sections: object[],
 *   setSections: (next: object[]) => void,
 * }} params
 */
export const useAttachmentWidgetUpload = ({ casePlanId, sections, setSections }) => {
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const removedIdsRef = useRef(new Set());
  const [attachmentViewer, setAttachmentViewer] = useState(null);

  const writeSections = useCallback(
    (nextSections) => {
      sectionsRef.current = nextSections;
      setSections(nextSections);
    },
    [setSections],
  );

  const updateWidgetItems = useCallback(
    (widgetId, updater) => {
      const current = findMappedWidgetById(sectionsRef.current, widgetId);
      if (!current) {
        return null;
      }
      const items = updater(current.props?.items ?? []);
      writeSections(replaceAttachmentWidgetItems(sectionsRef.current, widgetId, items));
      return items;
    },
    [writeSections],
  );

  const markItems = useCallback(
    (widgetId, ids, errorMessage) => {
      const idSet = new Set(ids);
      updateWidgetItems(widgetId, (items) =>
        items.map((item) =>
          idSet.has(item.id)
            ? {
                ...item,
                status: ATTACHMENT_ITEM_STATUS.ERROR,
                errorMessage,
              }
            : item,
        ),
      );
    },
    [updateWidgetItems],
  );

  const applyCommittedWidget = useCallback(
    (widgetId, response, committed) => {
      const current = findMappedWidgetById(sectionsRef.current, widgetId);
      const previousItems = current?.props?.items ?? [];
      const hydrated = hydrateCommittedWidget(response, previousItems, committed);
      const mapped = mapWidget(hydrated, {}, { readOnly: false });
      const successIds = new Set((mapped.props?.items ?? []).map((item) => item.id));
      const committedLocalIds = new Set(committed.map((file) => file.localId));
      const retainedInFlight = previousItems.filter(
        (item) =>
          !successIds.has(item.id) &&
          !committedLocalIds.has(item.id) &&
          (item.status === ATTACHMENT_ITEM_STATUS.ERROR ||
            item.status === ATTACHMENT_ITEM_STATUS.UPLOADING),
      );
      const nextWidget = {
        ...current,
        ...mapped,
        props: {
          ...current?.props,
          ...mapped.props,
          items: [...(mapped.props?.items ?? []), ...retainedInFlight],
        },
      };
      writeSections(replaceAttachmentWidget(sectionsRef.current, widgetId, nextWidget));
    },
    [writeSections],
  );

  const uploadAcceptedFiles = useCallback(
    async (widgetId, accepted) => {
      let presigned;
      try {
        presigned = await requestAttachmentPresignedUrls(
          widgetId,
          accepted.map((entry) => toPresignFile(entry.file)),
        );
      } catch {
        markItems(
          widgetId,
          accepted.map((entry) => entry.localId),
          ATTACHMENT_VALIDATION_MESSAGES.UPLOAD,
        );
        return;
      }

      const slots = Array.isArray(presigned?.attachments) ? presigned.attachments : [];
      const committed = [];

      await Promise.all(
        accepted.map(async (entry, index) => {
          const slot = slots[index];
          if (!slot?.uploadUrl || !slot?.id) {
            markItems(widgetId, [entry.localId], ATTACHMENT_VALIDATION_MESSAGES.UPLOAD);
            return;
          }
          try {
            await uploadFileToPresignedUrl({
              uploadUrl: slot.uploadUrl,
              file: entry.file,
              mimeType: entry.file.type,
            });
            committed.push({
              localId: entry.localId,
              id: slot.id,
              filename: entry.file.name,
              fileSize: entry.file.size,
              mimeType: entry.file.type,
            });
          } catch {
            markItems(widgetId, [entry.localId], ATTACHMENT_VALIDATION_MESSAGES.UPLOAD);
          }
        }),
      );

      const ready = committed.filter((file) => !removedIdsRef.current.has(file.localId));
      if (!ready.length || !casePlanId) {
        return;
      }

      try {
        const response = await commitWidgetAttachments(
          casePlanId,
          widgetId,
          ready.map(({ id, filename, fileSize, mimeType }) => ({
            id,
            filename,
            fileSize,
            mimeType,
          })),
        );
        applyCommittedWidget(widgetId, response, ready);
      } catch {
        markItems(
          widgetId,
          ready.map((file) => file.localId),
          ATTACHMENT_VALIDATION_MESSAGES.UPLOAD,
        );
      }
    },
    [applyCommittedWidget, casePlanId, markItems],
  );

  const onAddFiles = useCallback(
    async (widgetId, files) => {
      const selected = Array.from(files ?? []);
      if (!casePlanId || !widgetId || selected.length === 0) {
        return;
      }

      const currentItems = findMappedWidgetById(sectionsRef.current, widgetId)?.props?.items ?? [];
      const maxFiles = findMappedWidgetById(sectionsRef.current, widgetId)?.props?.maxFiles;
      let slotsUsed = currentItems.filter(occupiesAttachmentSlot).length;
      const accepted = [];
      const nextItems = [...currentItems];

      selected.forEach((file) => {
        const localId = createLocalAttachmentId();
        const validationMessage = validateAttachmentFile(file);
        if (validationMessage) {
          nextItems.push(toChip(localId, file, ATTACHMENT_ITEM_STATUS.ERROR, validationMessage));
          return;
        }
        if (typeof maxFiles === 'number' && slotsUsed >= maxFiles) {
          nextItems.push(
            toChip(
              localId,
              file,
              ATTACHMENT_ITEM_STATUS.ERROR,
              ATTACHMENT_VALIDATION_MESSAGES.MAX_FILES,
            ),
          );
          return;
        }
        slotsUsed += 1;
        accepted.push({ localId, file });
        nextItems.push(toChip(localId, file, ATTACHMENT_ITEM_STATUS.UPLOADING));
      });

      updateWidgetItems(widgetId, () => nextItems);

      if (accepted.length > 0) {
        await uploadAcceptedFiles(widgetId, accepted);
      }
    },
    [casePlanId, updateWidgetItems, uploadAcceptedFiles],
  );

  const onRemove = useCallback(
    async (widgetId, attachmentId) => {
      if (!attachmentId) {
        return;
      }

      const current = findMappedWidgetById(sectionsRef.current, widgetId);
      const item = (current?.props?.items ?? []).find((entry) => entry.id === attachmentId);
      if (!item) {
        return;
      }

      removedIdsRef.current.add(attachmentId);
      const snapshot = sectionsRef.current;
      updateWidgetItems(widgetId, (items) => items.filter((entry) => entry.id !== attachmentId));

      if (item.status !== ATTACHMENT_ITEM_STATUS.SUCCESS) {
        return;
      }

      try {
        await unlinkWidgetAttachment(casePlanId, widgetId, attachmentId);
      } catch {
        removedIdsRef.current.delete(attachmentId);
        writeSections(snapshot);
        store.dispatch(
          showToastWithType(ATTACHMENT_ACTION_ERROR_MESSAGES.REMOVE, TOAST_TYPE_ERROR),
        );
      }
    },
    [casePlanId, updateWidgetItems, writeSections],
  );

  const onOpen = useCallback(
    async (widgetId, attachmentId) => {
      const item = (findMappedWidgetById(sectionsRef.current, widgetId)?.props?.items ?? []).find(
        (entry) => entry.id === attachmentId,
      );
      if (!item || item.status !== ATTACHMENT_ITEM_STATUS.SUCCESS) {
        return;
      }

      try {
        const response = await fetchAttachmentDownloadUrl(attachmentId);
        const fileUrl = readDownloadUrl(response);
        if (!fileUrl) {
          throw new Error('Download URL missing');
        }
        setAttachmentViewer({
          fileUrl,
          fileName: item.fileName,
          fileType: detectFileType(item.fileName || item.mimeType || ''),
        });
      } catch {
        store.dispatch(showToastWithType(ATTACHMENT_ACTION_ERROR_MESSAGES.OPEN, TOAST_TYPE_ERROR));
      }
    },
    [],
  );

  const onCloseAttachmentViewer = useCallback(() => {
    setAttachmentViewer(null);
  }, []);

  return useMemo(
    () => ({
      onAddFiles,
      onRemove,
      onOpen,
      attachmentViewer,
      onCloseAttachmentViewer,
    }),
    [onAddFiles, onRemove, onOpen, attachmentViewer, onCloseAttachmentViewer],
  );
};
